import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Plus, Trash2, Send, CheckCircle2, AlertTriangle, FileDown, School, Users, ChefHat, BookOpen } from "lucide-react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import RelationPicker from "@/components/poshan/RelationPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_NUMBER = "918093414633";

const CATEGORY_CLASS_MAP = {
  "BV+Primary":                          ["balVatika", "primary"],
  "BV+Primary+Upper Primary":            ["balVatika", "primary", "upperPrimary"],
  "Upper Primary (6,7,8)":               ["upperPrimary"],
  "BV+Primary+Upper Primary+High School": ["balVatika", "primary", "upperPrimary", "highSchool"],
  "High School (IX-X)":                  ["highSchool"],
};
const ALL_CLASSES = ["balVatika", "primary", "upperPrimary", "highSchool"];

const VENDOR_CATEGORIES = [
  { value: "grocery", label: "Grocery (ଗ୍ରୋସେରୀ)" },
  { value: "vegetable", label: "Vegetable (ପରିବା)" },
  { value: "egg", label: "Egg (ଅଣ୍ଡା)" },
  { value: "fuel", label: "Fuel (ଜାଳେଣି)" },
  { value: "ladoo", label: "Ladoo (ଲଡୁ)" },
  { value: "other", label: "Other (ଅନ୍ୟ)" },
];

function PairField({ labelEn, labelOd, valueEn, valueOd, onEn, onOd, type = "text", testIdEn, testIdOd, required }) {
  const inputClass = "mt-1.5 block h-12 w-full rounded-lg border border-border bg-white px-3 text-sm text-ink-900 outline-none focus:border-clay focus:ring-1 focus:ring-clay/30";
  return (
    <div className="rounded-xl border border-border bg-white p-3">
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block">
          <span className="block text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ink-700">
            {labelEn} {required && <span className="text-clay">*</span>}
          </span>
          <input
            data-testid={testIdEn}
            type={type}
            inputMode={type === "number" ? "numeric" : undefined}
            value={valueEn ?? ""}
            onChange={(e) => onEn(type === "number" ? (Number(e.target.value) || 0) : e.target.value)}
            className={inputClass}
          />
        </label>
        {onOd && (
          <label className="block">
            <span className="block text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ink-700">{labelOd}</span>
            <input
              data-testid={testIdOd}
              type="text"
              value={valueOd ?? ""}
              onChange={(e) => onOd(e.target.value)}
              className={inputClass}
              lang="or"
            />
          </label>
        )}
      </div>
    </div>
  );
}

function SoloField({ label, value, onChange, type = "text", testId, hint }) {
  return (
    <label className="block rounded-xl border border-border bg-white p-3">
      <span className="block text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ink-700">{label}</span>
      <input
        data-testid={testId}
        type={type}
        inputMode={type === "number" ? "numeric" : undefined}
        value={value ?? ""}
        onChange={(e) => onChange(type === "number" ? (Number(e.target.value) || 0) : e.target.value)}
        className="mt-1.5 block h-12 w-full rounded-lg border border-border bg-white px-3 text-sm text-ink-900 outline-none focus:border-clay focus:ring-1 focus:ring-clay/30"
      />
      {hint && <p className="mt-1 text-[0.65rem] text-ink-700">{hint}</p>}
    </label>
  );
}

function Section({ title, subtitle, icon: Icon, children, testId }) {
  return (
    <section className="rounded-2xl border border-border bg-sand-50 p-4 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-5" data-testid={testId}>
      <header className="flex items-center gap-3">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-clay/15 text-clay"><Icon className="h-5 w-5" /></span>
        <div>
          <h2 className="font-heading text-base font-bold text-ink-900 sm:text-lg">{title}</h2>
          {subtitle && <p className="text-[0.7rem] text-ink-700">{subtitle}</p>}
        </div>
      </header>
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

const EMPTY_VENDOR = {
  name: "", nameOdia: "", category: "grocery",
  fatherName: "", fatherNameOdia: "", relationType: "father",
  village: "", villageOdia: "", post: "", postOdia: "",
  thana: "", thanaOdia: "", block: "", blockOdia: "",
  district: "", districtOdia: "", pincode: "", pincodeOdia: "",
  address: "", addressOdia: "",
};
const EMPTY_CCH = {
  name: "", nameOdia: "", relationType: "father", fatherName: "", fatherNameOdia: "",
  village: "", villageOdia: "", post: "", postOdia: "",
  thana: "", thanaOdia: "", block: "", blockOdia: "",
  district: "", districtOdia: "", pincode: "", pincodeOdia: "",
  address: "", addressOdia: "",
  gender: "", category: "", bankAccount: "", ifsc: "",
  monthlyHonorarium: 0, defaultMode: "Bank",
};

export default function PublicSetupForm() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [meta, setMeta] = useState(null);

  const [profile, setProfile] = useState({});
  const [vendors, setVendors] = useState([]);
  const [cchs, setCchs] = useState([]);
  // Refs for each A4 page of the printable PDF — 4 pages: identity+enrolment,
  // vendors 1-5, cooks 1-3, cooks 4-5.
  const pdfPageRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const [pdfBusy, setPdfBusy] = useState(false);

  const visibleClasses = useMemo(() => CATEGORY_CLASS_MAP[profile.category] || ALL_CLASSES, [profile.category]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/setup-tokens/${token}/preview`)
      .then((r) => {
        if (cancelled) return;
        setMeta(r.data.token);
        setProfile(r.data.profile || {});
        setVendors((r.data.vendors || []).map((v) => ({ ...EMPTY_VENDOR, ...v })));
        setCchs((r.data.cchs || []).map((c) => ({ ...EMPTY_CCH, ...c })));
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || "Could not open this link. It may be invalid.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token]);

  const setP = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const setV = (i, k, v) => setVendors((arr) => arr.map((row, idx) => idx === i ? { ...row, [k]: v } : row));
  const setC = (i, k, v) => setCchs((arr) => arr.map((row, idx) => idx === i ? { ...row, [k]: v } : row));

  const submit = async () => {
    if (!profile.schoolName?.trim()) { toast.error("School Name is required"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/setup-tokens/${token}/submit`, {
        profile,
        vendors: vendors.filter((v) => (v.name || "").trim()),
        cchs: cchs.filter((c) => (c.name || "").trim()),
      });
      setDone(true);
      toast.success("Submitted — awaiting admin review.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  // Blank PDF — multi-page A4 rendered from offscreen HTML templates so
  // Odia glyphs render correctly (jsPDF default fonts can't render Odia).
  const downloadPdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    try {
      const pdf = new jsPDF({ unit: "pt", format: "a4", compress: true });
      const A4W = 595; const A4H = 842;
      let firstPage = true;
      for (const ref of pdfPageRefs) {
        if (!ref.current) continue;
        const canvas = await html2canvas(ref.current, {
          scale: 2,
          backgroundColor: "#ffffff",
          useCORS: true,
          windowWidth: ref.current.scrollWidth,
          windowHeight: ref.current.scrollHeight,
        });
        const imgData = canvas.toDataURL("image/jpeg", 0.92);
        const imgW = A4W;
        const imgH = (canvas.height * A4W) / canvas.width;
        const renderH = Math.min(imgH, A4H);
        if (!firstPage) pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, 0, imgW, renderH, undefined, "FAST");
        firstPage = false;
      }
      pdf.save("PM-POSHAN-School-Setup-Form.pdf");
    } catch (err) {
      toast.error("PDF generation failed");
    } finally {
      setPdfBusy(false);
    }
  };

  if (loading) {
    return (
      <main className="grid min-h-screen place-items-center bg-sand-50 text-ink-900">
        <p className="text-sm">Loading setup link…</p>
      </main>
    );
  }
  if (error) {
    return (
      <main className="grid min-h-screen place-items-center bg-sand-50 px-4 text-ink-900">
        <div className="max-w-md rounded-2xl border border-destructive/30 bg-white p-6 text-center" data-testid="public-form-error">
          <AlertTriangle className="mx-auto h-10 w-10 text-destructive" />
          <p className="mt-3 font-heading text-lg font-bold">Link unavailable</p>
          <p className="mt-1 text-sm text-ink-700">{error}</p>
          <a href={`https://wa.me/${WA_NUMBER}`} target="_blank" rel="noreferrer" className="mt-4 inline-flex rounded-full bg-moss px-4 py-2 text-sm font-bold text-white">
            Contact us on WhatsApp
          </a>
        </div>
      </main>
    );
  }
  if (done) {
    return (
      <main className="grid min-h-screen place-items-center bg-sand-50 px-4 text-ink-900">
        <div className="max-w-md rounded-2xl border border-moss/30 bg-white p-6 text-center" data-testid="public-form-success">
          <CheckCircle2 className="mx-auto h-12 w-12 text-moss-dark" />
          <p className="mt-3 font-heading text-xl font-bold">Submitted for review</p>
          <p className="mt-2 text-sm text-ink-700">
            Thank you! Your details have been sent to the admin for verification.
            Once approved, your data will be updated automatically.
          </p>
          <p className="mt-3 text-xs text-ink-700">ଆମକୁ ସୂଚନା ପଠାଯାଇଛି। ଯଞ୍ଚ ପରେ ଆପଣଙ୍କ ତଥ୍ୟ ସଂଶୋଧନ କରାଯିବ।</p>
          <a href={`https://wa.me/${WA_NUMBER}`} target="_blank" rel="noreferrer" className="mt-4 inline-flex rounded-full bg-moss px-4 py-2 text-xs font-bold text-white">
            Contact us on WhatsApp
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-sand-50 text-ink-900" data-testid="public-setup-form">
      <header className="border-b border-border bg-white px-5 py-4">
        <p className="text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-clay">MDM Sahayak</p>
        <h1 className="mt-1 font-heading text-2xl font-bold text-ink-900">School Setup Form</h1>
        <p className="text-xs text-ink-700">ବିଦ୍ୟାଳୟ ସେଟଅପ ଫର୍ମ</p>
        <p className="mt-2 text-xs text-ink-700">
          <b className="text-ink-900">{meta?.schoolName || "—"}</b> · UDISE {meta?.udiseCode || "—"}
        </p>
        <p className="mt-3 rounded-lg bg-ochre/10 px-3 py-2 text-[0.7rem] text-ink-700">
          Fill in English <b>and</b> Odia where possible. Tap <b>Submit</b> when done — our admin will verify before applying.
          <br />
          ଦୟାକରି ଇଂରାଜୀ ଓ ଓଡ଼ିଆ ଉଭୟରେ ଲେଖନ୍ତୁ। ଯଞ୍ଚ ପରେ ଆପଣଙ୍କ ତଥ୍ୟ ସଂଶୋଧନ କରାଯିବ।
        </p>
      </header>

      <div className="mx-auto max-w-3xl space-y-5 px-3 py-6 sm:px-5">
        {/* SECTION 1 — School Identity */}
        <Section title="1. School Identity" subtitle="ବିଦ୍ୟାଳୟ ପରିଚୟ" icon={School} testId="public-section-profile">
          <PairField
            labelEn="School Name (English)" labelOd="ବିଦ୍ୟାଳୟ ନାମ (ଓଡ଼ିଆ)" required
            valueEn={profile.schoolName} valueOd={profile.schoolNameOdia}
            onEn={(v) => setP("schoolName", v)} onOd={(v) => setP("schoolNameOdia", v)}
            testIdEn="public-profile-school-name" testIdOd="public-profile-school-name-odia"
          />
          <SoloField label="UDISE Code" value={profile.udiseCode} onChange={(v) => setP("udiseCode", v)} testId="public-profile-udise" />
          <PairField
            labelEn="District (English)" labelOd="ଜିଲ୍ଲା (ଓଡ଼ିଆ)"
            valueEn={profile.district} valueOd={profile.districtOdia}
            onEn={(v) => setP("district", v)} onOd={(v) => setP("districtOdia", v)}
            testIdEn="public-profile-district" testIdOd="public-profile-district-odia"
          />
          <PairField
            labelEn="Block (English)" labelOd="ବ୍ଲକ (ଓଡ଼ିଆ)"
            valueEn={profile.block} valueOd={profile.blockOdia}
            onEn={(v) => setP("block", v)} onOd={(v) => setP("blockOdia", v)}
            testIdEn="public-profile-block" testIdOd="public-profile-block-odia"
          />
          <PairField
            labelEn="Village (English)" labelOd="ଗ୍ରାମ (ଓଡ଼ିଆ)"
            valueEn={profile.village} valueOd={profile.villageOdia}
            onEn={(v) => setP("village", v)} onOd={(v) => setP("villageOdia", v)}
            testIdEn="public-profile-village" testIdOd="public-profile-village-odia"
          />
          <PairField
            labelEn="Post (English)" labelOd="ପୋଷ୍ଟ (ଓଡ଼ିଆ)"
            valueEn={profile.post} valueOd={profile.postOdia}
            onEn={(v) => setP("post", v)} onOd={(v) => setP("postOdia", v)}
            testIdEn="public-profile-post" testIdOd="public-profile-post-odia"
          />
          <PairField
            labelEn="Thana / Police Station (English)" labelOd="ଥାନା (ଓଡ଼ିଆ)"
            valueEn={profile.thana} valueOd={profile.thanaOdia}
            onEn={(v) => setP("thana", v)} onOd={(v) => setP("thanaOdia", v)}
            testIdEn="public-profile-thana" testIdOd="public-profile-thana-odia"
          />
          <PairField
            labelEn="Pincode" labelOd="ପିନକୋଡ଼ (ଓଡ଼ିଆ)"
            valueEn={profile.pincode} valueOd={profile.pincodeOdia}
            onEn={(v) => setP("pincode", v)} onOd={(v) => setP("pincodeOdia", v)}
            testIdEn="public-profile-pincode" testIdOd="public-profile-pincode-odia"
          />
        </Section>

        {/* SECTION 2 — Class Enrolment */}
        <Section title="2. Class Enrolment" subtitle="ଶ୍ରେଣୀ ଅନୁସାରେ ଛାତ୍ର ସଂଖ୍ୟା" icon={BookOpen} testId="public-section-enrolment">
          {profile.category && <p className="text-xs text-ink-700">For category <b>{profile.category}</b></p>}
          <div className="grid gap-3 sm:grid-cols-2">
            {visibleClasses.includes("balVatika") && (
              <SoloField testId="public-enrol-bv" label="Bal Vatika  ·  ବାଲ ବାଟିକା" type="number" value={profile.balVatikaEnrollment} onChange={(v) => setP("balVatikaEnrollment", v)} />
            )}
            {visibleClasses.includes("primary") && (
              <SoloField testId="public-enrol-pri" label="Primary (I–V)  ·  ପ୍ରାଥମିକ" type="number" value={profile.primaryEnrollment} onChange={(v) => setP("primaryEnrollment", v)} />
            )}
            {visibleClasses.includes("upperPrimary") && (
              <SoloField testId="public-enrol-up" label="Upper Primary (VI–VIII)  ·  ଉଚ୍ଚ ପ୍ରାଥମିକ" type="number" value={profile.upperPrimaryEnrollment} onChange={(v) => setP("upperPrimaryEnrollment", v)} />
            )}
            {visibleClasses.includes("highSchool") && (
              <SoloField testId="public-enrol-hs" label="High School (IX–X)  ·  ଉଚ୍ଚ ବିଦ୍ୟାଳୟ" type="number" value={profile.highSchoolEnrollment} onChange={(v) => setP("highSchoolEnrollment", v)} />
            )}
          </div>
        </Section>

        {/* SECTION 3 — Vendors */}
        <Section title="3. Vendors" subtitle="ବ୍ୟବସାୟୀ ତାଲିକା — Grocery / Vegetable / Egg / Fuel" icon={Users} testId="public-section-vendors">
          {vendors.length === 0 && <p className="rounded-lg bg-white px-3 py-2 text-xs text-ink-700">No vendors yet. Tap "Add vendor" to begin.</p>}
          {vendors.map((v, i) => (
            <div key={i} className="rounded-xl border border-border bg-white p-3" data-testid={`public-vendor-${i}`}>
              <div className="flex items-center justify-between gap-2 pb-2">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Vendor #{i + 1}</p>
                <button type="button" onClick={() => setVendors((arr) => arr.filter((_, idx) => idx !== i))} data-testid={`public-vendor-${i}-remove`} className="grid h-8 w-8 place-items-center rounded-full text-destructive active:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-2.5">
                <PairField labelEn="Vendor Name (English)" labelOd="ବ୍ୟବସାୟୀ ନାମ (ଓଡ଼ିଆ)" required
                  valueEn={v.name} valueOd={v.nameOdia}
                  onEn={(val) => setV(i, "name", val)} onOd={(val) => setV(i, "nameOdia", val)}
                  testIdEn={`public-vendor-${i}-name`} testIdOd={`public-vendor-${i}-name-odia`} />
                {/* iter153 — Father / Husband picker for this vendor. */}
                <div>
                  <span className="mb-1.5 block text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Relation  ·  ସମ୍ପର୍କ</span>
                  <RelationPicker
                    value={v.relationType}
                    onChange={(val) => setV(i, "relationType", val)}
                    testIdPrefix={`public-vendor-${i}`}
                  />
                </div>
                <label className="block rounded-xl border border-border bg-sand-50 p-3">
                  <span className="block text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Category  ·  ବର୍ଗ</span>
                  <select value={v.category || "grocery"} onChange={(e) => setV(i, "category", e.target.value)}
                    data-testid={`public-vendor-${i}-category`}
                    className="mt-1.5 block h-12 w-full rounded-lg border border-border bg-white px-3 text-sm text-ink-900 outline-none focus:border-clay focus:ring-1 focus:ring-clay/30">
                    {VENDOR_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </label>
                <PairField labelEn={`${v.relationType === "husband" ? "Husband" : "Father"}'s Name (English)`} labelOd={v.relationType === "husband" ? "ସ୍ୱାମୀ ନାମ (ଓଡ଼ିଆ)" : "ପିତା ନାମ (ଓଡ଼ିଆ)"}
                  valueEn={v.fatherName} valueOd={v.fatherNameOdia}
                  onEn={(val) => setV(i, "fatherName", val)} onOd={(val) => setV(i, "fatherNameOdia", val)}
                  testIdEn={`public-vendor-${i}-father`} testIdOd={`public-vendor-${i}-father-odia`} />
                <PairField labelEn="Village (English)" labelOd="ଗ୍ରାମ (ଓଡ଼ିଆ)"
                  valueEn={v.village} valueOd={v.villageOdia}
                  onEn={(val) => setV(i, "village", val)} onOd={(val) => setV(i, "villageOdia", val)}
                  testIdEn={`public-vendor-${i}-village`} testIdOd={`public-vendor-${i}-village-odia`} />
                <PairField labelEn="Post (English)" labelOd="ପୋଷ୍ଟ (ଓଡ଼ିଆ)"
                  valueEn={v.post} valueOd={v.postOdia}
                  onEn={(val) => setV(i, "post", val)} onOd={(val) => setV(i, "postOdia", val)}
                  testIdEn={`public-vendor-${i}-post`} testIdOd={`public-vendor-${i}-post-odia`} />
                <PairField labelEn="Thana (English)" labelOd="ଥାନା (ଓଡ଼ିଆ)"
                  valueEn={v.thana} valueOd={v.thanaOdia}
                  onEn={(val) => setV(i, "thana", val)} onOd={(val) => setV(i, "thanaOdia", val)}
                  testIdEn={`public-vendor-${i}-thana`} testIdOd={`public-vendor-${i}-thana-odia`} />
                <PairField labelEn="Block (English)" labelOd="ବ୍ଲକ (ଓଡ଼ିଆ)"
                  valueEn={v.block} valueOd={v.blockOdia}
                  onEn={(val) => setV(i, "block", val)} onOd={(val) => setV(i, "blockOdia", val)}
                  testIdEn={`public-vendor-${i}-block`} testIdOd={`public-vendor-${i}-block-odia`} />
                <PairField labelEn="District (English)" labelOd="ଜିଲ୍ଲା (ଓଡ଼ିଆ)"
                  valueEn={v.district} valueOd={v.districtOdia}
                  onEn={(val) => setV(i, "district", val)} onOd={(val) => setV(i, "districtOdia", val)}
                  testIdEn={`public-vendor-${i}-district`} testIdOd={`public-vendor-${i}-district-odia`} />
                <PairField labelEn="Pincode" labelOd="ପିନକୋଡ଼ (ଓଡ଼ିଆ)"
                  valueEn={v.pincode} valueOd={v.pincodeOdia}
                  onEn={(val) => setV(i, "pincode", val)} onOd={(val) => setV(i, "pincodeOdia", val)}
                  testIdEn={`public-vendor-${i}-pincode`} testIdOd={`public-vendor-${i}-pincode-odia`} />
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setVendors((arr) => [...arr, { ...EMPTY_VENDOR }])} data-testid="public-add-vendor"
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-clay/40 bg-white py-3 text-xs font-bold uppercase tracking-wider text-clay hover:bg-clay/5">
            <Plus className="h-3.5 w-3.5" /> Add vendor  ·  ବ୍ୟବସାୟୀ ଯୋଗନ୍ତୁ
          </button>
        </Section>

        {/* SECTION 4 — CCH */}
        <Section title="4. Cook-cum-Helpers" subtitle="ରୋଷେୟା ତାଲିକା" icon={ChefHat} testId="public-section-cchs">
          {cchs.length === 0 && <p className="rounded-lg bg-white px-3 py-2 text-xs text-ink-700">No cooks yet. Tap "Add cook" to begin.</p>}
          {cchs.map((c, i) => (
            <div key={i} className="rounded-xl border border-border bg-white p-3" data-testid={`public-cch-${i}`}>
              <div className="flex items-center justify-between gap-2 pb-2">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Cook #{i + 1}</p>
                <button type="button" onClick={() => setCchs((arr) => arr.filter((_, idx) => idx !== i))} data-testid={`public-cch-${i}-remove`} className="grid h-8 w-8 place-items-center rounded-full text-destructive active:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="space-y-2.5">
                <PairField labelEn="Cook Name (English)" labelOd="ରୋଷେୟା ନାମ (ଓଡ଼ିଆ)" required
                  valueEn={c.name} valueOd={c.nameOdia}
                  onEn={(val) => setC(i, "name", val)} onOd={(val) => setC(i, "nameOdia", val)}
                  testIdEn={`public-cch-${i}-name`} testIdOd={`public-cch-${i}-name-odia`} />
                {/* iter153 — Father / Husband picker for this cook. */}
                <div>
                  <span className="mb-1.5 block text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Relation  ·  ସମ୍ପର୍କ</span>
                  <RelationPicker
                    value={c.relationType}
                    onChange={(val) => setC(i, "relationType", val)}
                    testIdPrefix={`public-cch-${i}`}
                  />
                </div>
                <PairField labelEn={`${c.relationType === "husband" ? "Husband" : "Father"}'s Name (English)`} labelOd={c.relationType === "husband" ? "ସ୍ୱାମୀ ନାମ (ଓଡ଼ିଆ)" : "ପିତା ନାମ (ଓଡ଼ିଆ)"}
                  valueEn={c.fatherName} valueOd={c.fatherNameOdia}
                  onEn={(val) => setC(i, "fatherName", val)} onOd={(val) => setC(i, "fatherNameOdia", val)}
                  testIdEn={`public-cch-${i}-father`} testIdOd={`public-cch-${i}-father-odia`} />
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="block rounded-xl border border-border bg-sand-50 p-3">
                    <span className="block text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Gender  ·  ଲିଙ୍ଗ</span>
                    <select value={c.gender || ""} onChange={(e) => setC(i, "gender", e.target.value)}
                      data-testid={`public-cch-${i}-gender`}
                      className="mt-1.5 block h-12 w-full rounded-lg border border-border bg-white px-3 text-sm text-ink-900">
                      <option value="">—</option>
                      <option value="M">Male (M)</option>
                      <option value="F">Female (F)</option>
                    </select>
                  </label>
                  <label className="block rounded-xl border border-border bg-sand-50 p-3">
                    <span className="block text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-ink-700">Category  ·  ବର୍ଗ</span>
                    <select value={c.category || ""} onChange={(e) => setC(i, "category", e.target.value)}
                      data-testid={`public-cch-${i}-category`}
                      className="mt-1.5 block h-12 w-full rounded-lg border border-border bg-white px-3 text-sm text-ink-900">
                      <option value="">—</option>
                      <option value="SC">SC</option>
                      <option value="ST">ST</option>
                      <option value="OBC">OBC</option>
                      <option value="GEN">GEN</option>
                    </select>
                  </label>
                </div>
                <PairField labelEn="Village (English)" labelOd="ଗ୍ରାମ (ଓଡ଼ିଆ)"
                  valueEn={c.village} valueOd={c.villageOdia}
                  onEn={(val) => setC(i, "village", val)} onOd={(val) => setC(i, "villageOdia", val)}
                  testIdEn={`public-cch-${i}-village`} testIdOd={`public-cch-${i}-village-odia`} />
                <PairField labelEn="Post (English)" labelOd="ପୋଷ୍ଟ (ଓଡ଼ିଆ)"
                  valueEn={c.post} valueOd={c.postOdia}
                  onEn={(val) => setC(i, "post", val)} onOd={(val) => setC(i, "postOdia", val)}
                  testIdEn={`public-cch-${i}-post`} testIdOd={`public-cch-${i}-post-odia`} />
                <PairField labelEn="Thana (English)" labelOd="ଥାନା (ଓଡ଼ିଆ)"
                  valueEn={c.thana} valueOd={c.thanaOdia}
                  onEn={(val) => setC(i, "thana", val)} onOd={(val) => setC(i, "thanaOdia", val)}
                  testIdEn={`public-cch-${i}-thana`} testIdOd={`public-cch-${i}-thana-odia`} />
                <PairField labelEn="Block (English)" labelOd="ବ୍ଲକ (ଓଡ଼ିଆ)"
                  valueEn={c.block} valueOd={c.blockOdia}
                  onEn={(val) => setC(i, "block", val)} onOd={(val) => setC(i, "blockOdia", val)}
                  testIdEn={`public-cch-${i}-block`} testIdOd={`public-cch-${i}-block-odia`} />
                <PairField labelEn="District (English)" labelOd="ଜିଲ୍ଲା (ଓଡ଼ିଆ)"
                  valueEn={c.district} valueOd={c.districtOdia}
                  onEn={(val) => setC(i, "district", val)} onOd={(val) => setC(i, "districtOdia", val)}
                  testIdEn={`public-cch-${i}-district`} testIdOd={`public-cch-${i}-district-odia`} />
                <PairField labelEn="Pincode" labelOd="ପିନକୋଡ଼ (ଓଡ଼ିଆ)"
                  valueEn={c.pincode} valueOd={c.pincodeOdia}
                  onEn={(val) => setC(i, "pincode", val)} onOd={(val) => setC(i, "pincodeOdia", val)}
                  testIdEn={`public-cch-${i}-pincode`} testIdOd={`public-cch-${i}-pincode-odia`} />
                <SoloField testId={`public-cch-${i}-honorarium`} label="Monthly Honorarium (₹)  ·  ମାସିକ ଦରମା" type="number" value={c.monthlyHonorarium} onChange={(val) => setC(i, "monthlyHonorarium", val)} />
                <SoloField testId={`public-cch-${i}-bank`} label="Bank A/c No. (optional · confidential)  ·  ବ୍ୟାଙ୍କ ଖାତା ସଂଖ୍ୟା" value={c.bankAccount} onChange={(val) => setC(i, "bankAccount", val)} />
                <SoloField testId={`public-cch-${i}-ifsc`} label="IFSC Code (optional · confidential)  ·  IFSC କୋଡ୍" value={c.ifsc} onChange={(val) => setC(i, "ifsc", val)} />
              </div>
            </div>
          ))}
          <button type="button" onClick={() => setCchs((arr) => [...arr, { ...EMPTY_CCH }])} data-testid="public-add-cch"
            className="flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-clay/40 bg-white py-3 text-xs font-bold uppercase tracking-wider text-clay hover:bg-clay/5">
            <Plus className="h-3.5 w-3.5" /> Add cook  ·  ରୋଷେୟା ଯୋଗନ୍ତୁ
          </button>
        </Section>

        <div className="space-y-3 pb-16">
          <Button onClick={submit} disabled={submitting} data-testid="public-submit-btn" className="w-full gap-2 bg-clay py-6 text-sm font-bold uppercase tracking-[0.12em] text-white hover:bg-clay-dark disabled:opacity-50">
            <Send className="h-4 w-4" /> {submitting ? "Sending…" : "Submit setup"}
          </Button>
          <button onClick={downloadPdf} disabled={pdfBusy} data-testid="public-download-pdf" className="flex w-full items-center justify-center gap-2 rounded-2xl border border-clay/30 bg-white py-3 text-xs font-bold text-clay disabled:opacity-60">
            <FileDown className="h-4 w-4" /> {pdfBusy ? "Preparing PDF…" : "Download blank form (PDF)  ·  ଖାଲି ଫର୍ମ PDF"}
          </button>
          <p className="text-center text-[0.7rem] text-ink-700">
            Can't fill online? Download the form, write the details on paper, photograph and send to WhatsApp <b>8093414633</b>.
          </p>
        </div>
      </div>

      {/* Offscreen 2-page A4 template for PDF (html2canvas → jsPDF). */}
      <PdfTemplate pageRefs={pdfPageRefs} meta={meta} />
    </main>
  );
}

// ---------- Offscreen A4 PDF template ----------
// Rendered absolutely positioned far off-screen but inside the DOM so
// html2canvas can capture it. A4 width ≈ 794px @ 96dpi.
function PdfRow({ en, od, half }) {
  return (
    <div style={{
      flex: half ? "1 1 0" : "1 1 100%",
      border: "1px solid #9a9a9a",
      borderRadius: 4,
      padding: "6px 8px",
      height: 56,
      marginRight: half ? 6 : 0,
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-start",
      background: "#ffffff",
    }}>
      <div style={{ fontSize: 9.5, fontWeight: 700, color: "#7a3e2a", letterSpacing: 0.2 }}>{en}</div>
      <div style={{ fontSize: 9, color: "#9b6b58", marginTop: 1 }}>{od}</div>
    </div>
  );
}
function PdfSectionHeader({ n, en, od }) {
  return (
    <div style={{
      background: "#f5ebdc",
      borderLeft: "4px solid #b4503c",
      padding: "8px 12px",
      margin: "14px 0 10px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#7a3e2a" }}>{n}. {en}</div>
      <div style={{ fontSize: 11, color: "#5a5a5a" }}>{od}</div>
    </div>
  );
}
function PdfPageShell({ children, footer }) {
  return (
    <div style={{
      width: 794, minHeight: 1123, padding: 38, boxSizing: "border-box",
      background: "#ffffff", color: "#222", fontFamily: "Noto Sans, Inter, Arial, sans-serif",
      border: "2px solid #b4503c",
    }}>
      <div style={{
        background: "#b4503c", color: "#fff", padding: "12px 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: 1 }}>MDM SAHAYAK</div>
          <div style={{ fontSize: 11, marginTop: 2 }}>School Setup Form  ·  ବିଦ୍ୟାଳୟ ସେଟଅପ ଫର୍ମ</div>
        </div>
        <div style={{ textAlign: "right", fontSize: 10, opacity: 0.85 }}>
          mdmsahayak.com
        </div>
      </div>
      <div style={{ padding: "10px 2px 4px", fontSize: 10, color: "#555", display: "flex", justifyContent: "space-between" }}>
        <span>Fill in English & Odia.  ·  ଇଂରାଜୀ ଓ ଓଡ଼ିଆ ଦୁଇଟି ଲେଖନ୍ତୁ।</span>
        <span>Send filled photo → WhatsApp 8093414633</span>
      </div>
      <div>{children}</div>
      <div style={{ marginTop: 14, paddingTop: 8, borderTop: "1px dashed #c8b39d", display: "flex", justifyContent: "space-between", fontSize: 9, color: "#7d7d7d" }}>
        <span>MDM Sahayak — mdmsahayak.com</span>
        <span>{footer}</span>
      </div>
    </div>
  );
}
function PdfTemplate({ pageRefs, meta }) {
  const wrap = { display: "flex", gap: 0, marginBottom: 8 };
  const TOTAL = 4;
  const VendorBlock = (i) => (
    <div key={`v${i}`} style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#7a3e2a", margin: "4px 0 6px" }}>Vendor {i}  ·  ବ୍ୟବସାୟୀ {i}</div>
      <div style={wrap}>
        <PdfRow half en="Vendor Name (English)" od="ବ୍ୟବସାୟୀ ନାମ (ଇଂରାଜୀ)" />
        <PdfRow half en="Vendor Name (Odia)" od="ବ୍ୟବସାୟୀ ନାମ (ଓଡ଼ିଆ)" />
      </div>
      <div style={wrap}>
        <PdfRow half en="Category" od="ବର୍ଗ (grocery / vegetable / egg / fuel / ladoo)" />
        <PdfRow half en="☐ Father / ପିତା    ☐ Husband / ସ୍ୱାମୀ    — Name" od="ପିତା / ସ୍ୱାମୀ ନାମ" />
      </div>
      <div style={wrap}>
        <PdfRow half en="Village / Post / Thana" od="ଗ୍ରାମ / ପୋଷ୍ଟ / ଥାନା" />
        <PdfRow half en="Block / District / Pincode" od="ବ୍ଲକ / ଜିଲ୍ଲା / ପିନକୋଡ଼" />
      </div>
    </div>
  );
  const CookBlock = (i) => (
    <div key={`c${i}`} style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#7a3e2a", margin: "4px 0 6px" }}>Cook {i}  ·  ରୋଷେୟା {i}</div>
      <div style={wrap}>
        <PdfRow half en="Cook Name (English)" od="ରୋଷେୟା ନାମ (ଇଂରାଜୀ)" />
        <PdfRow half en="Cook Name (Odia)" od="ରୋଷେୟା ନାମ (ଓଡ଼ିଆ)" />
      </div>
      <div style={wrap}>
        <PdfRow half en="☐ Father / ପିତା    ☐ Husband / ସ୍ୱାମୀ    — Name" od="ପିତା / ସ୍ୱାମୀ ନାମ" />
        <PdfRow half en="Gender  ·  Category (SC/ST/OBC/GEN)" od="ଲିଙ୍ଗ  ·  ବର୍ଗ" />
      </div>
      <div style={wrap}>
        <PdfRow half en="Village / Post / Thana" od="ଗ୍ରାମ / ପୋଷ୍ଟ / ଥାନା" />
        <PdfRow half en="Block / District / Pincode" od="ବ୍ଲକ / ଜିଲ୍ଲା / ପିନକୋଡ଼" />
      </div>
      <div style={wrap}>
        <PdfRow half en="Monthly Honorarium (₹)" od="ମାସିକ ଦରମା (₹)" />
        <PdfRow half en="Bank A/c No.  ·  IFSC  (confidential)" od="ବ୍ୟାଙ୍କ ଖାତା · IFSC  (ଗୋପନୀୟ)" />
      </div>
    </div>
  );

  return (
    <div style={{ position: "absolute", left: -10000, top: 0 }} aria-hidden>
      {/* PAGE 1 — School Identity + Class Enrolment */}
      <div ref={pageRefs[0]}>
        <PdfPageShell footer={`Page 1 / ${TOTAL}`}>
          <div style={{ display: "flex", justifyContent: "space-between", margin: "4px 0 10px", fontSize: 10, color: "#444" }}>
            <span>Issued for:  <b>{meta?.schoolName || "____________________"}</b></span>
            <span>UDISE:  <b>{meta?.udiseCode || "______________"}</b></span>
          </div>

          <PdfSectionHeader n="1" en="School Identity" od="ବିଦ୍ୟାଳୟ ପରିଚୟ" />
          <div style={wrap}>
            <PdfRow half en="School Name (English)" od="ବିଦ୍ୟାଳୟ ନାମ (ଇଂରାଜୀ)" />
            <PdfRow half en="School Name (Odia)" od="ବିଦ୍ୟାଳୟ ନାମ (ଓଡ଼ିଆ)" />
          </div>
          <div style={wrap}>
            <PdfRow half en="UDISE Code" od="UDISE କୋଡ୍" />
            <PdfRow half en="Category" od="ବର୍ଗ (BV+Primary / Upper Primary …)" />
          </div>
          <div style={wrap}>
            <PdfRow half en="District (English)" od="ଜିଲ୍ଲା (ଇଂରାଜୀ)" />
            <PdfRow half en="District (Odia)" od="ଜିଲ୍ଲା (ଓଡ଼ିଆ)" />
          </div>
          <div style={wrap}>
            <PdfRow half en="Block (English)" od="ବ୍ଲକ (ଇଂରାଜୀ)" />
            <PdfRow half en="Block (Odia)" od="ବ୍ଲକ (ଓଡ଼ିଆ)" />
          </div>
          <div style={wrap}>
            <PdfRow half en="Village (English)" od="ଗ୍ରାମ (ଇଂରାଜୀ)" />
            <PdfRow half en="Village (Odia)" od="ଗ୍ରାମ (ଓଡ଼ିଆ)" />
          </div>
          <div style={wrap}>
            <PdfRow half en="Post (English)" od="ପୋଷ୍ଟ (ଇଂରାଜୀ)" />
            <PdfRow half en="Post (Odia)" od="ପୋଷ୍ଟ (ଓଡ଼ିଆ)" />
          </div>
          <div style={wrap}>
            <PdfRow half en="Thana / Police Station (English)" od="ଥାନା (ଇଂରାଜୀ)" />
            <PdfRow half en="Thana / Police Station (Odia)" od="ଥାନା (ଓଡ଼ିଆ)" />
          </div>
          <div style={wrap}>
            <PdfRow half en="Pincode" od="ପିନକୋଡ଼" />
            <PdfRow half en="Pincode (Odia)" od="ପିନକୋଡ଼ (ଓଡ଼ିଆ)" />
          </div>

          <PdfSectionHeader n="2" en="Class Enrolment" od="ଶ୍ରେଣୀ ଅନୁସାରେ ଛାତ୍ର ସଂଖ୍ୟା" />
          <div style={wrap}>
            <PdfRow half en="Bal Vatika" od="ବାଲ ବାଟିକା" />
            <PdfRow half en="Primary (I – V)" od="ପ୍ରାଥମିକ" />
          </div>
          <div style={wrap}>
            <PdfRow half en="Upper Primary (VI – VIII)" od="ଉଚ୍ଚ ପ୍ରାଥମିକ" />
            <PdfRow half en="High School (IX – X)" od="ଉଚ୍ଚ ବିଦ୍ୟାଳୟ" />
          </div>
        </PdfPageShell>
      </div>

      {/* PAGE 2 — Vendors 1-5 */}
      <div ref={pageRefs[1]}>
        <PdfPageShell footer={`Page 2 / ${TOTAL}`}>
          <PdfSectionHeader n="3" en="Vendors  (Grocery / Vegetable / Egg / Fuel / Ladoo)" od="ବ୍ୟବସାୟୀ ତାଲିକା (୫ ପର୍ଯ୍ୟନ୍ତ)" />
          {[1, 2, 3, 4, 5].map(VendorBlock)}
        </PdfPageShell>
      </div>

      {/* PAGE 3 — Cooks 1-3 */}
      <div ref={pageRefs[2]}>
        <PdfPageShell footer={`Page 3 / ${TOTAL}`}>
          <PdfSectionHeader n="4" en="Cook-cum-Helpers  (1 – 3)" od="ରୋଷେୟା ତାଲିକା (୧ – ୩)" />
          {[1, 2, 3].map(CookBlock)}
        </PdfPageShell>
      </div>

      {/* PAGE 4 — Cooks 4-5 */}
      <div ref={pageRefs[3]}>
        <PdfPageShell footer={`Page 4 / ${TOTAL}`}>
          <PdfSectionHeader n="4" en="Cook-cum-Helpers  (4 – 5)" od="ରୋଷେୟା ତାଲିକା (୪ – ୫)" />
          {[4, 5].map(CookBlock)}
        </PdfPageShell>
      </div>
    </div>
  );
}
